import { formatDateStringToYYYYMMDD } from '@/utils/date';
import { currentNewsletterIso, getNewsletterStatus } from '@/utils/newsletter';
import { SOI, DOI, CONFIRMED } from '@/utils/subscription-types';

export default {
  provide() {
    return {
      newsletter: this.newsletter,
    };
  },
  data: () => ({
    newsletterErrors: {},
  }),
  computed: {
    ...mapState(useMainStore, {
      language: 'language',
      configuration: 'configuration',
      features: 'getFeature',
      pageTemplate: 'pageTemplate',
      newsletterRecaptchaEnabled: 'getNewsletterRecaptchaEnabled',
    }),

    ...mapState(useUserStore, {
      user: 'user',
      isAuthenticated: 'getIsAuthenticated',
      isSoftLoggedIn: 'softLogin',
    }),

    ...mapWritableState(useNewsletterStore, {
      hasNewsletterSubscription: 'hasNewsletterSubscription',
      newsletterSubscriptionChecked: 'newsletterSubscriptionChecked',
    }),

    showNewsletterCheckbox() {
      return !this.hasNewsletterSubscription && this.newsletterSubscriptionChecked;
    },

    showNewsletterRecaptcha() {
      return this.newsletterRecaptchaEnabled && this.showNewsletterCheckbox;
    },

    newsletterISO() {
      if (this.configuration?.language?.available && this.language) {
        return currentNewsletterIso(this.configuration.language.available, this.language);
      }
      return null;
    },

    newsletterRegisterData() {
      return {
        address: {
          emailAddress: this.user.uid,
        },
        consent: true,
        channel: 'EMAIL',
        email: '',
        interests: [],
        offers: [],
        recipient: {
          birthday: this.user?.defaultAddress?.dateOfBirth
            ? formatDateStringToYYYYMMDD(this.user.defaultAddress.dateOfBirth)
            : null,
          firstname: this.user?.defaultAddress?.firstName || null,
          lastname: this.user?.defaultAddress?.lastName || null,
          status: 'NEW',
          sessionLanguage: this.newsletterISO,
        },
        segments: [],
        pets: [],
        stores: [],
        source: '',
      };
    },

    isFriendsLightTemplate() {
      return this.pageTemplate === 'friends-light';
    },
  },
  methods: {
    ...mapActions(useSubscriptionStore, {
      newsletterCcaSubscription: 'status',
    }),

    ...mapActions(useNewsletterStore, {
      setNewsletterRegistration: 'register',
      newsletterUnsubscribe: 'unsubscribe',
      setNewsletterSubscription: 'subscription',
      newsletterUpdate: 'update',
    }),

    registerToNewsletter(recaptchaFields) {
      const newsletterData = this.newsletterRegisterData;
      if (recaptchaFields && this.newsletterRecaptchaEnabled) {
        newsletterData.recaptchaResponse = recaptchaFields.recaptchaResponse;
      }

      return this.createAsyncRegistrationPromise(newsletterData);
    },

    async createAsyncRegistrationPromise(newsletterData) {
      try {
        await this.setNewsletterRegistration(newsletterData);
        await this.checkNewsletterStatus();
        this.newsletterErrors = {};
        return true;
      } catch (error) {
        if (error?.response?.status === 403) {
          this.newsletterErrors = {
            general: [this.$t('forms.newsletter.registration.error.alreadyRegistered')],
          };
        } else {
          this.newsletterErrors = {
            general: [this.$t('validation.general')],
          };
        }

        this.$log.error('Error while registering newsletter.', error);

        return new Error(error);
      }
    },

    async checkNewsletterStatus() {
      const isLoggedIn = this.isAuthenticated || this.isSoftLoggedIn;
      const shouldFetchStatus = this.features('checkoutNewsletterEnabled') && isLoggedIn;
      let newsletterSubscription = false;

      try {
        if (!shouldFetchStatus) {
          return;
        }
        const newsletterStatus = await getNewsletterStatus(this.user.cryptId);

        switch (newsletterStatus?.type) {
          case DOI:
            // Allow user to activate newsletter when deactivated
            if (this.isFriendsLightTemplate) {
              newsletterSubscription = true;
              break;
            }
            newsletterSubscription = newsletterStatus?.recipient?.status === CONFIRMED;
            break;

          case SOI:
            newsletterSubscription = this.isFriendsLightTemplate;
            break;

          default:
            newsletterSubscription = true;
        }
      } catch (error) {
        this.$log.error(error);
      } finally {
        this.hasNewsletterSubscription = newsletterSubscription;
        this.newsletterSubscriptionChecked = true;
      }
    },
  },
};
